import { RouteContext, RouteParams, RouterContext } from 'universal-router';

import { AppViewMode, MCP_SELECT_WORKSPACE_PAGE } from 'src/constants';
import { isRouteActionContext } from 'src/routes/types';

// TODO: Establish a pattern for per-route param typing.
//
//       In the meantime, make all route params global
//       and (probably) optional.
export interface GlobalRouteParams {
  description?: string;
  id?: string;
  redirect?: string;
  title?: string;
}
// The top-level (parent) route

/**
 * This method is used to redirect to a new route with query params
 * @param pathTo new path to redirect to
 * @param context router context
 * @returns redirect object
 */
const redirectWithQueryParams = (pathTo: string, context: RouterContext) => {
  const { query } = context;
  const queryParams = new URLSearchParams(query);
  const params = queryParams.toString();
  return { redirect: `${pathTo}?${params}` };
};
const routes = {
  path: '',

  // Keep in mind, routes are evaluated in order
  children: [
    {
      path: '',
      load: () => import(/* webpackChunkName: 'home' */ './home'),
    },
    {
      path: '/playground',
      load: () => import(/* webpackChunkName: 'playground' */ './playground'),
    },
    {
      path: '/new',
      load: () => import(/* webpackChunkName: 'home-new' */ './home-new'),
    },
    {
      path: '/portal',
      children: [
        {
          path: '/:portalId',
          load: () => import(/* webpackChunkName: 'home' */ './home'),
        },
      ],
    },
    {
      path: '/workspaces',
      load: () =>
        import(/* webpackChunkName: 'onboarding-users' */ './onboarding/users'),
    },
    // for backward compatibility if someone is saving a bookmark with the old route
    // e.g. /clients/users/details?clientUserId=123
    // we will redirect to the new route
    // e.g. /clients/users/details/123
    {
      path: '/clients/users/details',
      action: (
        context: RouteContext<GlobalRouteParams, RouterContext>,
        _: RouteParams,
      ) => {
        // extract clientUserId from query params
        const clientUserId =
          context.query && 'clientUserId' in context.query
            ? context.query.clientUserId
            : '';
        if (clientUserId && typeof clientUserId === 'string') {
          return { redirect: `/clients/users/details/${clientUserId}` };
        }
        return { redirect: '/clients/users' }; // fallback if no ID
      },
    },
    {
      path: '/clients',
      children: [
        {
          path: '/users',
          children: [
            {
              path: '',
              load: () => import(/* webpackChunkName: 'users' */ './users'),
            },

            {
              path: '/details',
              children: [
                {
                  path: '',
                  load: () =>
                    import(
                      /* webpackChunkName: 'client-details-page' */ './users/ClientsDetailsPage'
                    ),
                },

                {
                  path: '/:clientUserId/:tab?',
                  load: () =>
                    import(
                      /* webpackChunkName: 'client-details-page' */ './users/ClientsDetailsPage'
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    // Top-level import route (supports both client and company imports)
    {
      path: '/import',
      load: () =>
        import(
          /* webpackChunkName: 'client-imports-page' */ './users/ClientImportPage'
        ),
    },
    // Backward compatibility redirect from old /clients/import path
    {
      path: '/clients/import',
      action(context: RouterContext) {
        const { EnableCompanyImport } = context.flags;
        if (EnableCompanyImport) {
          return { redirect: '/import' };
        }
        // When flag is off, load the original client import page directly
        return import(
          /* webpackChunkName: 'client-imports-page' */ './users/ClientImportPage'
        ).then((module) => {
          if (isRouteActionContext(context)) {
            return module.default(context);
          }
          return { redirect: '/' };
        });
      },
    },
    {
      path: '/companies',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'companies' */ './companies'),
        },
        {
          path: '/:companyId/:tab?',
          load: () =>
            import(
              /* webpackChunkName: 'company-details' */ './companies/details'
            ),
        },
      ],
    },
    {
      path: '/products',
      children: [
        {
          path: '',
          action: () => ({ redirect: '/payments/services' }),
        },
        {
          path: '/edit',
          action: () => ({ redirect: '/payments/services/edit' }),
        },
        {
          path: '/details/:id',
          action: (context: RouterContext) => ({
            redirect: `/payments/services/details/${context.params.id}`,
          }),
        },
      ],
    },
    {
      path: '/invoices',
      children: [
        {
          path: '',
          action: async (context: RouterContext) => {
            const isClient =
              context.portalConfig.viewMode === AppViewMode.CLIENT;

            if (!isClient) {
              return { redirect: '/payments/invoices' };
            }
            return { redirect: '/billing' };
          },
        },
        {
          path: '/details',
          action: (context: RouterContext) =>
            redirectWithQueryParams('/payments/invoices/details', context),
        },
        {
          path: '/pay',
          load: () =>
            import(
              /* webpackChunkName: 'invoices-pay' */ './payments/invoices/pay/action'
            ),
        },
        {
          path: '/edit',
          children: [
            {
              path: '',
              action: (context: RouterContext) =>
                redirectWithQueryParams('/payments/invoices/edit', context),
            },
            {
              path: '/template',
              action: (context: RouterContext) =>
                redirectWithQueryParams(
                  '/payments/invoices/edit/template',
                  context,
                ),
            },
          ],
        },
      ],
    },
    {
      path: '/payments',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'payments' */ './payments'),
        },
        {
          path: '/invoices',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-invoices' */ './payments/invoices'
                ),
            },
            {
              path: '/details',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-invoice-details' */ './payments/invoices/details'
                ),
            },
            {
              path: '/edit',
              children: [
                {
                  path: '',
                  load: () =>
                    import(
                      /* webpackChunkName: 'payment-invoice-edit' */ './payments/invoices/edit'
                    ),
                },
                {
                  path: '/template',
                  load: () =>
                    import(
                      /* webpackChunkName: 'payment-invoice-template' */ './payments/invoices/template'
                    ),
                },
              ],
            },
          ],
        },
        {
          path: '/subscriptions',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-subscriptions' */ './payments/subscriptions'
                ),
            },
            {
              path: '/edit',
              children: [
                {
                  path: '',
                  load: () =>
                    import(
                      /* webpackChunkName: 'payment-subscription-edit' */ './payments/subscriptions/edit'
                    ),
                },
                {
                  path: '/template',
                  load: () =>
                    import(
                      /* webpackChunkName: 'payment-subscription-template' */ './payments/subscriptions/template'
                    ),
                },
              ],
            },
          ],
        },
        {
          path: '/store',
          load: () =>
            import(/* webpackChunkName: 'payment-store' */ './payments/store'),
        },
        {
          path: '/payment-links',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-payment-links' */ './payments/payment-links'
                ),
            },
            {
              path: '/new',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-payment-links-new' */ './payments/payment-links/new'
                ),
            },
            {
              path: '/edit',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-payment-links-edit' */ './payments/payment-links/edit'
                ),
            },
          ],
        },
        {
          path: '/services',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-services' */ './payments/services'
                ),
            },
            {
              path: '/edit',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-service-edit' */ './payments/services/edit'
                ),
            },
            {
              path: '/details/:id',
              load: () =>
                import(
                  /* webpackChunkName: 'payment-service-details' */ './payments/services/service-details'
                ),
            },
          ],
        },
      ],
    },
    {
      path: '/billing',
      children: [
        {
          path: '',
          load: () =>
            import(
              /* webpackChunkName: 'billing' */ './payments/client-billing/action'
            ),
        },
      ],
    },
    {
      path: '/subscriptions',
      children: [
        {
          path: '',
          action: () => ({ redirect: '/payments/subscriptions' }),
        },
        {
          path: '/edit',
          children: [
            {
              path: '',
              action: (context: RouterContext) =>
                redirectWithQueryParams(
                  '/payments/subscriptions/edit',
                  context,
                ),
            },
            {
              path: '/template',
              action: (context: RouterContext) =>
                redirectWithQueryParams(
                  '/payments/subscriptions/edit/template',
                  context,
                ),
            },
          ],
        },
      ],
    },
    {
      path: '/login',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'login' */ './auth'),
        },
        {
          path: '/access-link',
          load: () =>
            import(
              /* webpackChunkName: 'auth-access-link' */ './auth/access-link'
            ),
        },
      ],
    },
    {
      path: '/browser-unsupported',
      load: () =>
        import(
          /* webpackChunkName: 'browser-unsupported' */ './browser-unsupported'
        ),
    },
    // Following routes serve as redirection to helpdesk route as knowledge base it self has a path change
    {
      path: '/knowledge-base',
      children: [
        {
          path: '',
          action() {
            return { redirect: '/helpdesk' };
          },
        },
        {
          path: '/article/:id',
          // Optional only for Typescript's benefit, we can count
          // on this existing in the JS runtime.
          action({ params }: { params: { id?: string } }) {
            return { redirect: `/helpdesk/article/${params.id}` };
          },
        },
      ],
    },
    {
      path: '/helpdesk',
      children: [
        {
          path: '',
          load: () =>
            import(/* webpackChunkName: 'knowledge-base' */ './knowledgebase'),
        },
        {
          path: '/article/:id',
          load: () => import(/* webpackChunkName: 'article' */ './article'),
        },
      ],
    },
    {
      path: '/files',
      load: () => import(/* webpackChunkName: 'files' */ './files'),
    },
    {
      path: '/automations',
      children: [
        {
          path: '',
          load: () =>
            import(/* webpackChunkName: 'automations' */ './automations'),
        },
        {
          path: '/edit',
          load: () =>
            import(
              /* webpackChunkName: 'automations-edit' */ './automations/edit'
            ),
        },
        {
          path: '/details',
          load: () =>
            import(
              /* webpackChunkName: 'automations-runs' */ './automations/runs'
            ),
        },
        {
          path: '/runs/details',
          load: () =>
            import(
              /* webpackChunkName: 'automations-details' */ './automations/details'
            ),
        },
      ],
    },
    {
      path: '/contracts',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'contracts' */ './contracts'),
        },
        {
          path: '/edit',
          load: () =>
            import(/* webpackChunkName: 'contract-edit' */ './contracts/edit'),
        },
        {
          path: '/details',
          load: () =>
            import(
              /* webpackChunkName: 'contract-details' */ './contracts/contract-details'
            ),
        },
        {
          path: '/submit',
          load: () =>
            import(
              /* webpackChunkName: 'contract-submit' */ './contracts/submit'
            ),
        },
        {
          path: '/submission',
          load: () =>
            import(
              /* webpackChunkName: 'contract-submission' */ './contracts/submission'
            ),
        },
      ],
    },
    {
      path: '/notifications',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'inbox' */ './inbox'),
        },
      ],
    },
    // This unified route pattern (/preview/:appName) enables contextual previews
    // based on the originating app (e.g., notifications, etc.).
    // Dynamically loads the 'file-preview' module for the given app name
    {
      path: '/preview/:appName',
      load: () =>
        import(/* webpackChunkName: 'notification-preview' */ './file-preview'),
    },
    {
      path: '/apps',
      load: () => import(/* webpackChunkName: 'extensions' */ './apps'),
    },
    {
      path: '/extensions',
      action: (context: RouterContext) =>
        redirectWithQueryParams('/apps', context),
    },
    {
      path: '/dev-mode',
      load: () => import(/* webpackChunkName: 'extensions' */ './dev-mode'),
    },
    {
      path: '/messaging',
      load: () => import(/* webpackChunkName: 'messaging' */ './messaging'),
    },
    {
      path: '/signup',
      load: () =>
        import(/* webpackChunkName: 'onboarding-setup' */ './onboarding/setup'),
    },
    {
      path: '/admin',
      load: () => import(/* webpackChunkName: 'admin' */ './admin'),
    },
    {
      path: '/add-account',
      load: () =>
        import(/* webpackChunkName: 'onboarding-login' */ './onboarding/login'),
    },
    {
      path: '/forms',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'forms' */ './forms'),
        },
        {
          path: '/edit',
          load: () =>
            import(/* webpackChunkName: 'form-edit' */ './forms/edit'),
        },
        {
          path: '/response',
          load: () =>
            import(/* webpackChunkName: 'form-response' */ './forms/response'),
        },
        {
          path: '/details',
          load: () =>
            import(
              /* webpackChunkName: 'form-details' */ './forms/form-details'
            ),
        },
        {
          path: '/submit',
          load: () =>
            import(/* webpackChunkName: 'form-submit' */ './forms/submit'),
        },
      ],
    },
    // Customization and modules being a standalone route instead of child of settings.
    // The original route are still available for backward compatibility.
    {
      path: '/customization',
      load: () =>
        import(
          /* webpackChunkName: 'settings-customization' */ './settings/customization'
        ),
    },

    {
      path: '/payment-links',
      children: [
        {
          path: '',
          action: () => ({ redirect: '/payments/payment-links' }),
        },
        {
          path: '/new',
          action: () => ({ redirect: '/payments/payment-links/new' }),
        },
        {
          path: '/edit',
          action: () => ({ redirect: '/payments/payment-links/edit' }),
        },
        {
          path: '/pay/:id/:title',
          load: () =>
            import(
              /* webpackChunkName: 'payment-links-pay' */ './payments/payment-links/pay/action'
            ),
        },
      ],
    },
    {
      path: '/store',
      children: [
        {
          path: '',
          async action(context: RouterContext) {
            const isClient =
              context.portalConfig.viewMode === AppViewMode.CLIENT;

            if (!isClient) {
              return { redirect: '/payments/store' };
            }

            const storeModule = await import(
              /* webpackChunkName: 'store' */ './payments/store/client/action'
            );
            if (isRouteActionContext(context)) {
              return storeModule.default(context);
            }

            return { redirect: '/' };
          },
        },
        {
          path: '/:id',
          load: () =>
            import(
              /* webpackChunkName: 'client-product-store' */ './payments/store/client/action'
            ),
        },
        {
          path: '/:storeId/pay',
          load: () =>
            import(
              /* webpackChunkName: 'client-product-store-pay' */ './payments/store/pay/action'
            ),
        },
      ],
    },
    {
      path: '/app-setup',
      children: [
        {
          path: '',
          load: () =>
            import(/* webpackChunkName: 'app-setup' */ './appSetup/setup'),
        },
        {
          path: '/new',
          load: () =>
            import(
              /* webpackChunkName: 'app-setup-new-module' */ './appSetup/new-app'
            ),
        },
        {
          path: '/build',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'app-builder' */ './appSetup/builder'
                ),
            },
            {
              path: '/session/:id',
              load: () =>
                import(
                  /* webpackChunkName: 'app-builder-session' */ './appSetup/builder/session'
                ),
            },
          ],
        },
        {
          path: '/setup',
          load: () =>
            import(/* webpackChunkName: 'setup-app' */ './appSetup/setup-app'),
        },
        {
          path: '/:appId',
          load: () =>
            import(
              /* webpackChunkName: 'app-details' */ './appSetup/app-details'
            ),
        },
      ],
    },
    {
      path: '/referrals',
      load: () => import(/* webpackChunkName: 'referrals' */ './referrals'),
    },
    {
      path: '/settings',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'settings' */ './settings'),
        },
        {
          path: '/profile',
          load: () =>
            import(
              /* webpackChunkName: 'settings-account' */ './settings/account'
            ),
        },
        // OLD ROUTE
        {
          path: '/me',
          action() {
            return { redirect: '/settings/profile' };
          },
        },
        {
          path: '/general',
          load: () =>
            import(
              /* webpackChunkName: 'settings-main' */ './settings/general'
            ),
        },
        {
          path: '/client-authentication',
          load: () =>
            import(
              /* webpackChunkName: 'client-authentication' */ './settings/auth'
            ),
        },
        {
          path: '/assistant-settings',
          load: () =>
            import(
              /* webpackChunkName: 'assistant-settings' */ './settings/assistant-settings'
            ),
        },
        {
          path: '/client-notifications',
          load: () =>
            import(
              /* webpackChunkName: 'settings-client-notifications' */ './settings/client-notifications'
            ),
        },
        // OLD ROUTE
        {
          path: '/main',
          action() {
            return { redirect: '/settings/general' };
          },
        },
        {
          path: '/domains',
          load: () =>
            import(
              /* webpackChunkName: 'settings-domain' */ './settings/domain'
            ),
        },
        // OLD ROUTE
        {
          path: '/domain',
          action() {
            return { redirect: '/settings/domains' };
          },
        },
        {
          path: '/team',
          load: () =>
            import(/* webpackChunkName: 'settings-team' */ './settings/team'),
        },
        {
          path: '/events',
          load: () =>
            import(
              /* webpackChunkName: 'settings-events' */ './settings/events'
            ),
        },
        // OLD ROUTE
        {
          path: '/members',
          action() {
            return { redirect: '/settings/team' };
          },
        },
        {
          path: '/notifications',

          load: () =>
            import(
              /* webpackChunkName: 'settings-notifications' */ './settings/notifications'
            ),
        },
        {
          path: '/api',
          children: [
            {
              path: '',
              load: () =>
                import(/* webpackChunkName: 'settings-api' */ './settings/api'),
            },
            {
              path: '/webhooks/edit/:id',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-api-webhooks' */ './settings/api/webhooks'
                ),
            },
            {
              path: '/webhooks',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-api-webhooks' */ './settings/api/webhooks'
                ),
            },
          ],
        },
        {
          path: '/apps',
          load: () =>
            import(/* webpackChunkName: 'settings-apps' */ './settings/apps'),
        },
        {
          path: '/plans',
          children: [
            {
              path: '',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-plans' */ './settings/plans'
                ),
            },
            {
              path: '/cancel',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-plans-cancel-plan-start' */ './settings/plans/cancel-plan-start'
                ),
            },
            {
              path: '/cancel/confirm',
              load: () =>
                import(
                  /* webpackChunkName: 'settings-plans-cancel-plan-confirm' */ './settings/plans/cancel-plan-confirm'
                ),
            },
          ],
        },
        // OLD ROUTE
        {
          path: '/plan',
          children: [
            {
              path: '',
              action() {
                return { redirect: '/settings/plans' };
              },
            },
            {
              path: '/cancel',
              action() {
                return { redirect: '/settings/plans/cancel' };
              },
            },
            {
              path: '/cancel/confirm',
              action() {
                return { redirect: '/settings/plans/cancel/confirm' };
              },
            },
          ],
        },
        {
          path: '/referral',
          action: () => ({ redirect: '/referrals' }),
        },
        {
          path: '/payout-account',
          load: () =>
            import(
              /* webpackChunkName: 'settings-payments' */ './settings/payments'
            ),
        },
        // OLD ROUTE
        {
          path: '/payments',
          action() {
            return { redirect: '/settings/payout-account' };
          },
        },
        {
          path: '/billing',
          load: () =>
            import(
              /* webpackChunkName: 'settings-billing' */ './settings/billing'
            ),
        },
      ],
    },
    {
      path: '/install/:slug',
      load: () => import(/* webpackChunkName: 'install' */ './install'),
    },
    {
      path: '/sso/:app',
      load: () => import(/* webpackChunkName: 'sso' */ './sso'),
    },
    {
      path: MCP_SELECT_WORKSPACE_PAGE,
      load: () =>
        import(
          /* webpackChunkName: 'mcp-select-workspace' */ './mcp/select-workspace'
        ),
    },
    {
      path: '/no-apps',
      load: () => import(/* webpackChunkName: 'no-apps' */ './no-apps'),
    },
    // Wildcard routes, e.g. { path: '(.*)', ... } (must go last)
    {
      path: '(.*)',
      load: () => import(/* webpackChunkName: 'not-found' */ './not-found'),
    },
  ],

  async action({ next }: { next: () => Promise<RouterContext> }) {
    // Execute each child route until one of them return the result
    const route = await next();

    // Provide default values for title, description etc.
    route.title = `${route.title || 'Untitled Page'}`;
    route.description = route.description || '';

    return route;
  },
};

// The error page is available by permanent url for development mode
if (__DEV__) {
  routes.children.unshift({
    path: '/error',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    action: require('./error').default,
  });
}

export default routes;
